import { Link } from "react-router-dom";
import { ArrowRight, Building2 } from "lucide-react";
import { InlineCopy } from "@/components/LocationSharedBlocks";

/**
 * Body sections for /industries and /industries/{sector} (handover v3).
 *
 * Presentational only — copy comes from data/industriesContent.js. Each
 * section takes a `bg` so the page can alternate white / grey bands.
 */

const CONTAINER = "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8";
const EYEBROW = "text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3";
const H2 = "font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight";

const paragraphs = (body) => (Array.isArray(body) ? body : [body]);

function SectionHeading({ eyebrow, heading, intro }) {
  return (
    <div className="max-w-2xl mb-8">
      {eyebrow && <p className={EYEBROW}>{eyebrow}</p>}
      <h2 className={H2}>{heading}</h2>
      {intro && <p className="text-sm text-brand-grey leading-relaxed mt-3">{intro}</p>}
    </div>
  );
}

/** H2 with a grid of H3 cards. */
export function IndustryCardsSection({ section, bg = "bg-white", testId }) {
  const cols = section.items.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2";
  const cardBg = bg === "bg-white" ? "bg-gray-50" : "bg-white";
  return (
    <section className={`py-14 md:py-16 ${bg}`} data-testid={testId}>
      <div className={CONTAINER}>
        <SectionHeading {...section} />
        <div className={`grid grid-cols-1 ${cols} gap-4`}>
          {section.items.map((item) => (
            <div key={item.heading} className={`${cardBg} border border-brand-light-grey rounded-2xl p-6`}>
              <h3 className="font-heading text-lg font-bold text-brand-black mb-2">{item.heading}</h3>
              <div className="space-y-3">
                {paragraphs(item.body).map((p) => (
                  <p key={p} className="text-sm text-brand-grey leading-relaxed">
                    <InlineCopy text={p} />
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/** H2 with one or more paragraphs of running copy. */
export function IndustryProseSection({ section, bg = "bg-white", testId }) {
  return (
    <section className={`py-14 md:py-16 ${bg}`} data-testid={testId}>
      <div className={CONTAINER}>
        <div className="max-w-3xl">
          <h2 className={`${H2} mb-5`}>{section.heading}</h2>
          <div className="space-y-4">
            {section.paragraphs.map((p) => (
              <p key={p} className="text-base text-brand-grey leading-relaxed">
                <InlineCopy text={p} />
              </p>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/** H2 with named customer examples (CanDo-authored, not quotes). */
export function IndustryExamplesSection({ section, bg = "bg-gray-50", testId }) {
  const cols = section.items.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2";
  const cardBg = bg === "bg-white" ? "bg-gray-50" : "bg-white";
  return (
    <section className={`py-14 md:py-16 ${bg}`} data-testid={testId}>
      <div className={CONTAINER}>
        <SectionHeading heading={section.heading} />
        <div className={`grid grid-cols-1 ${cols} gap-4`}>
          {section.items.map((c) => (
            <div key={c.name} className={`${cardBg} border border-brand-light-grey rounded-2xl p-6 flex items-start gap-4`}>
              <div className="w-10 h-10 rounded-xl bg-brand-aqua-light flex items-center justify-center flex-shrink-0">
                <Building2 size={18} className="text-brand-aqua-dark" aria-hidden="true" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold text-brand-black mb-1.5">{c.name}</h3>
                <p className="text-sm text-brand-grey leading-relaxed">{c.body}</p>
              </div>
            </div>
          ))}
        </div>
        {section.note && <p className="text-sm text-brand-grey mt-4">{section.note}</p>}
      </div>
    </section>
  );
}

const RENDERERS = {
  cards: IndustryCardsSection,
  prose: IndustryProseSection,
  examples: IndustryExamplesSection,
};

/** Render a page's `sections` array in order, alternating backgrounds. */
export function IndustrySections({ sections, testIdPrefix }) {
  return sections.map((section, i) => {
    const Section = RENDERERS[section.type];
    return (
      <Section
        key={section.heading}
        section={section}
        bg={i % 2 === 0 ? "bg-white" : "bg-gray-50"}
        testId={`${testIdPrefix}-section-${i + 1}`}
      />
    );
  });
}

/** "Related:" pill links shown after the FAQs. */
export function IndustryRelatedLinks({ links, testId }) {
  return (
    <section className="py-10 bg-gray-50 border-y border-brand-light-grey/50" data-testid={testId}>
      <div className={CONTAINER}>
        <nav aria-label="Related pages" className="flex flex-col sm:flex-row sm:items-center gap-4">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua flex-shrink-0">Related</p>
          <div className="flex flex-wrap gap-2">
            {links.map((l) => {
              const isHub = l.to === "/industries";
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  className={
                    isHub
                      ? "inline-flex items-center gap-1.5 rounded-full bg-brand-aqua-light text-brand-aqua-dark hover:bg-brand-aqua hover:text-white text-sm font-semibold px-4 py-2 transition-colors"
                      : "inline-flex items-center gap-1.5 rounded-full border border-brand-light-grey bg-white hover:border-brand-aqua/50 hover:bg-brand-aqua-light/50 text-sm font-semibold text-brand-black px-4 py-2 transition-colors"
                  }
                >
                  {l.label}
                  {isHub && <ArrowRight size={14} />}
                </Link>
              );
            })}
          </div>
        </nav>
      </div>
    </section>
  );
}
